import { Injectable, OnDestroy } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'environments/environment';
import { JwtAuthService } from 'app/api/services/cookie-jwt-auth/jwt-auth.service';

export interface SupportTicketNotification {
    ticketId: number;
    title: string;
    clientName: string;
    clientProject: string;
    severity: string;
    category: string;
}

@Injectable({
    providedIn: 'root'
})
export class SignalRService implements OnDestroy {
    private hubConnection: HubConnection;
    private connectionState$ = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
    private handlersRegistered = false;

    // Event subjects for different notification types
    public notification$ = new Subject<any>();
    public supportTicket$ = new Subject<SupportTicketNotification>();

    // Live collaborative editing (Yjs relay) — keyed by task content session.
    public contentUpdate$ = new Subject<{ key: string; update: string }>();
    public awareness$ = new Subject<{ key: string; awareness: string }>();

    private _disabledLogShown = false;

    constructor(private jwtAuth: JwtAuthService) {}

    public async startConnection(): Promise<void> {
        if (environment.signalREnabled === false) {
            if (!this._disabledLogShown) {
                console.warn('SignalR is disabled via environment.signalREnabled=false');
                this._disabledLogShown = true;
            }
            this.connectionState$.next(HubConnectionState.Disconnected);
            return;
        }

        if (this.hubConnection?.state === HubConnectionState.Connected) {
            return;
        }

        // Prevent creating multiple connections simultaneously
        if (this.hubConnection?.state === HubConnectionState.Connecting) {
            return;
        }

        const hubUrl = `${environment.apiURL}/hubs/notifications`;

        this.hubConnection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => this.jwtAuth.token || '',
                // Allow fallback to other transports if WebSockets fail
                transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
                withCredentials: true
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(environment.production ? LogLevel.Error : LogLevel.Information)
            .build();

        // Only register handlers once
        if (!this.handlersRegistered) {
            this.registerHandlers();
            this.registerConnectionEvents();
            this.handlersRegistered = true;
        }

        try {
            await this.hubConnection.start();
            console.log('SignalR connected');
            this.connectionState$.next(this.hubConnection.state);
        } catch (err) {
            console.error('SignalR connection error:', err);
            // Retry after 5 seconds
            setTimeout(() => this.startConnection(), 5000);
        }
    }

    public async stopConnection(): Promise<void> {
        if (this.hubConnection) {
            await this.hubConnection.stop();
            console.log('SignalR disconnected');
            this.connectionState$.next(HubConnectionState.Disconnected);
            this.handlersRegistered = false; // Reset flag so handlers can be registered on reconnect
        }
    }

    private registerHandlers(): void {
        // Handle system notifications (task assignments, comments, etc.)
        this.hubConnection.on('ReceiveNotification', (notification: any) => {
            console.log('Received notification:', notification);
            this.notification$.next(notification);
        });

        // Handle new support ticket notifications
        this.hubConnection.on('NewSupportTicket', (ticket: SupportTicketNotification) => {
            console.log('New support ticket:', ticket);
            this.supportTicket$.next(ticket);
        });

        // Collaborative editing relay
        this.hubConnection.on('ReceiveContentUpdate', (key: string, update: string) => {
            this.contentUpdate$.next({ key, update });
        });
        this.hubConnection.on('ReceiveAwareness', (key: string, awareness: string) => {
            this.awareness$.next({ key, awareness });
        });
    }

    private registerConnectionEvents(): void {
        this.hubConnection.onreconnecting((error) => {
            console.log('SignalR reconnecting...', error);
            this.connectionState$.next(HubConnectionState.Reconnecting);
        });

        this.hubConnection.onreconnected((connectionId) => {
            console.log('SignalR reconnected:', connectionId);
            this.connectionState$.next(HubConnectionState.Connected);
        });

        this.hubConnection.onclose((error) => {
            console.log('SignalR connection closed:', error);
            this.connectionState$.next(HubConnectionState.Disconnected);
            this.handlersRegistered = false; // Reset flag on connection close
        });
    }

    // Join a project group to receive project-specific updates
    public async joinProjectGroup(projectId: number): Promise<void> {
        if (this.hubConnection?.state === HubConnectionState.Connected) {
            await this.hubConnection.invoke('JoinProjectGroup', projectId);
        }
    }

    // Leave a project group
    public async leaveProjectGroup(projectId: number): Promise<void> {
        if (this.hubConnection?.state === HubConnectionState.Connected) {
            await this.hubConnection.invoke('LeaveProjectGroup', projectId);
        }
    }

    // ── Collaborative editing session relay ──

    /** Resolve once the hub is connected (starts the connection if needed). */
    private async ensureConnected(): Promise<void> {
        await this.startConnection();
        if (this.hubConnection?.state === HubConnectionState.Connected) return;
        await new Promise<void>((resolve) => {
            const sub = this.connectionState$.subscribe((state) => {
                if (state === HubConnectionState.Connected) {
                    sub.unsubscribe();
                    resolve();
                }
            });
        });
    }

    public async joinContentSession(key: string): Promise<{ shouldSeed: boolean; updates: string[] }> {
        await this.ensureConnected();
        const res: any = await this.hubConnection.invoke('JoinContentSession', key);
        return {
            shouldSeed: res?.shouldSeed ?? res?.ShouldSeed ?? false,
            updates: res?.updates ?? res?.Updates ?? [],
        };
    }

    public submitContentUpdate(key: string, update: string): void {
        if (this.hubConnection?.state === HubConnectionState.Connected) {
            this.hubConnection.invoke('SubmitContentUpdate', key, update).catch((e) => console.error('SubmitContentUpdate failed', e));
        }
    }

    public submitAwareness(key: string, awareness: string): void {
        if (this.hubConnection?.state === HubConnectionState.Connected) {
            this.hubConnection.invoke('SubmitAwareness', key, awareness).catch(() => {});
        }
    }

    public async leaveContentSession(key: string): Promise<void> {
        if (this.hubConnection?.state === HubConnectionState.Connected) {
            await this.hubConnection.invoke('LeaveContentSession', key).catch(() => {});
        }
    }

    public getConnectionState(): BehaviorSubject<HubConnectionState> {
        return this.connectionState$;
    }

    public isConnected(): boolean {
        return this.hubConnection?.state === HubConnectionState.Connected;
    }

    ngOnDestroy(): void {
        this.stopConnection();
    }
}
